/**
 * Service API pour communiquer avec le backend Django.
 * SÉCURITÉ: Logs désactivés en production via logger sécurisé
 */
import axios from 'axios';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const BASE_URL = API_URL.replace('/api', ''); // URL de base sans /api pour les médias

logger.log('[API] Configuration API URL:', API_URL);
logger.log('[API] Configuration BASE URL:', BASE_URL);

/**
 * Construit l'URL complète d'un fichier média
 * @param {string} mediaPath - Chemin relatif du média (ex: "media/photos/enfants/photo.jpg")
 * @returns {string} URL complète du média
 */
export const getMediaUrl = (mediaPath) => {
  if (!mediaPath) return null;
  // Si l'URL est déjà complète (http/https), la retourner telle quelle
  if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://')) {
    return mediaPath;
  }
  // Sinon, construire l'URL complète
  // Retirer le slash au début si présent
  const cleanPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
  return `${BASE_URL}/${cleanPath}`;
};

// Instance axios avec configuration de base
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 secondes timeout
  withCredentials: true, // SÉCURITÉ: Envoyer les cookies httpOnly avec chaque requête
  xsrfCookieName: 'csrftoken', // Nom du cookie CSRF Django
  xsrfHeaderName: 'X-CSRFToken', // Nom du header attendu par Django
});

// Intercepteur pour ajouter le token JWT aux requêtes
api.interceptors.request.use(
  (config) => {
    // DÉVELOPPEMENT: En dev, utiliser localStorage + headers pour contourner les problèmes de cookies cross-port
    // PRODUCTION: Utiliser uniquement les cookies httpOnly (sécurité maximale)
    const accessToken = localStorage.getItem('access_token');
    if (accessToken && !config.url?.includes('/auth/login/') && !config.url?.includes('/auth/register/')) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      logger.log('[API] Token ajouté depuis localStorage');
    }

    logger.log(`[API] Request ${config.method?.toUpperCase()} ${config.url}`);

    // Si on envoie du FormData, supprimer le Content-Type pour laisser le navigateur gérer
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      logger.log('[API] FormData détecté, Content-Type supprimé pour multipart/form-data');
    }

    // Log des données envoyées
    if (config.data && config.method !== 'get') {
      logger.log('[API] Data envoyée:', config.data);
    }

    return config;
  },
  (error) => {
    logger.error('[API] Erreur dans request interceptor:', error);
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer le rafraîchissement automatique du token
api.interceptors.response.use(
  (response) => {
    logger.log(`[API] Response ${response.status} pour ${response.config.url}`);
    if (response.data) {
      logger.log('[API] Data reçue:', response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    logger.error(`[API] Erreur ${error.response?.status} pour ${originalRequest?.url}`, error.response?.data);

    // Éviter la boucle infinie sur les endpoints d'authentification
    if (originalRequest?.url?.includes('/auth/login/') ||
      originalRequest?.url?.includes('/auth/register/') ||
      originalRequest?.url?.includes('/auth/2fa/') ||
      originalRequest?.url?.includes('/auth/refresh/')) {
      return Promise.reject(error);
    }

    // SÉCURITÉ: Tenter de rafraîchir le token automatiquement en cas de 401
    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      try {
        logger.log('[API] Tentative de refresh du token...');
        // Appeler l'endpoint de refresh qui lit le refresh_token depuis les cookies
        const refreshResponse = await api.post('/auth/refresh/');

        // En développement, stocker le nouveau token depuis le body
        if (refreshResponse.data?.access) {
          localStorage.setItem('access_token', refreshResponse.data.access);
          logger.log('[API] Nouveau token stocké depuis le body de la réponse');
        }

        logger.log('[API] Token refreshed avec succès, nouvelle tentative de la requête originale');
        // Le nouveau access_token est maintenant dans les cookies OU dans localStorage
        // Réessayer la requête originale
        return api(originalRequest);
      } catch (refreshError) {
        logger.error('[API] Échec du refresh token:', refreshError);

        // Le refresh token est expiré ou invalide, déconnecter l'utilisateur
        // Supprimer les tokens de localStorage
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        logger.log('[API] Tokens supprimés de localStorage');

        // CORRECTION: Ne PAS rediriger vers /login si la requête échouée est getCurrentUser
        // (c'est juste une vérification d'auth, pas une vraie tentative d'accès à une ressource protégée)
        const isAuthCheck = originalRequest?.url?.includes('/auth/me/') || originalRequest?.url?.includes('/users/me/');
        const currentPath = window.location.pathname;

        if (!isAuthCheck && !currentPath.includes('/login') && !currentPath.includes('/register')) {
          logger.log('[API] Refresh token expiré, redirection vers /login');
          window.location.href = '/login';
        } else {
          logger.log('[API] 401 sur vérification auth, pas de redirection nécessaire');
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Authentification
export const authService = {
  login: (email, password) => {
    logger.log('[AUTH] Login attempt:', email);
    return api.post('/auth/login/', { email, password });
  },
  verify2FA: (email, code) => {
    logger.log('[AUTH] Verifying 2FA code for:', email);
    return api.post('/auth/2fa/verify/', { email, code });
  },
  resend2FA: (email) => {
    logger.log('[AUTH] Resending 2FA code for:', email);
    return api.post('/auth/2fa/resend/', { email });
  },
  register: (userData) => {
    logger.log('[AUTH] Register attempt:', userData.email);
    return api.post('/auth/register/', userData);
  },
  logout: () => {
    logger.log('[AUTH] Logout');
    // SÉCURITÉ: Les tokens sont dans les cookies httpOnly
    // On fait un appel au backend pour les supprimer
    return api.post('/auth/logout/');
  },
  getCurrentUser: () => {
    logger.log('[AUTH] Getting current user');
    return api.get('/users/me/');
  },
  requestPasswordReset: (email) => {
    logger.log('[AUTH] Requesting password reset for:', email);
    return api.post('/auth/password-reset/', { email });
  },
  confirmPasswordReset: (token, password) => {
    logger.log('[AUTH] Confirming password reset');
    return api.post('/auth/password-reset/confirm/', {
      token,
      password,
      password_confirm: password
    });
  },
};

// Gestion des membres (Admin)
export const userService = {
  getAll: (params = {}) => {
    logger.log('[USER] Getting all users');
    return api.get('/admin/users/', { params });
  },
  getOne: (id) => {
    logger.log('[USER] Getting user', id);
    return api.get(`/admin/users/${id}/`);
  },
  update: (id, data) => {
    logger.log('[USER] Updating user', id, data);
    return api.patch(`/admin/users/${id}/`, data);
  },
  delete: (id) => {
    logger.log('[USER] Deleting user', id);
    return api.delete(`/admin/users/${id}/`);
  },
  toggleActive: (id) => {
    logger.log('[USER] Toggling active status for user', id);
    return api.post(`/admin/users/${id}/toggle_active/`);
  },
  resetPassword: (id, newPassword) => {
    logger.log('[USER] Resetting password for user', id);
    return api.post(`/admin/users/${id}/reset_password/`, { new_password: newPassword });
  },
  getEnfants: (id) => {
    logger.log('[USER] Getting enfants for user', id);
    return api.get(`/admin/users/${id}/enfants/`);
  },
  affecterEnfant: (userId, enfantId) => {
    logger.log('[USER] Affecting enfant', enfantId, 'to user', userId);
    return api.post(`/admin/users/${userId}/affecter_enfant/`, { enfant_id: enfantId });
  },
  desaffecterEnfant: (userId, enfantId) => {
    logger.log('[USER] Unassigning enfant', enfantId, 'from user', userId);
    return api.post(`/admin/users/${userId}/desaffecter_enfant/`, { enfant_id: enfantId });
  },
  supprimerEnfant: (userId, enfantId) => {
    logger.log('[USER] Deleting enfant', enfantId, 'from user', userId);
    return api.post(`/admin/users/${userId}/supprimer_enfant/`, { enfant_id: enfantId });
  },
  sendPasswordResetEmail: (userId) => {
    logger.log('[USER] Sending password reset email to user', userId);
    return api.post(`/admin/users/${userId}/send_password_reset_email/`);
  },
};

// Séjours
export const sejourService = {
  getAll: () => {
    logger.log('[SEJOUR] Getting all séjours');
    return api.get('/sejours/');
  },
  getOne: (id) => {
    logger.log('[SEJOUR] Getting séjour', id);
    return api.get(`/sejours/${id}/`);
  },
  create: (data) => {
    logger.log('[SEJOUR] Creating séjour', data);
    return api.post('/sejours/', data);
  },
  createWithFile: (formData) => {
    logger.log('[SEJOUR] Creating séjour with file upload');
    return api.post('/sejours/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  update: (id, data) => {
    logger.log('[SEJOUR] Updating séjour', id, data);
    return api.put(`/sejours/${id}/`, data);
  },
  updateWithFile: (id, formData) => {
    logger.log('[SEJOUR] Updating séjour with file upload', id);
    return api.put(`/sejours/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (id) => {
    logger.log('[SEJOUR] Deleting séjour', id);
    return api.delete(`/sejours/${id}/`);
  },
  getStatistiquesPlaces: (id) => {
    logger.log('[SEJOUR] Getting statistiques places for séjour', id);
    return api.get(`/sejours/${id}/statistiques_places/`);
  },
  getStatistiquesPlacesTous: () => {
    logger.log('[SEJOUR] Getting statistiques places for all séjours');
    return api.get('/sejours/statistiques_places_tous/');
  },
  toggleActif: (id) => {
    logger.log('[SEJOUR] Toggling actif status for séjour', id);
    return api.post(`/sejours/${id}/toggle_actif/`);
  },
  updatePartenaires: (id, partenaireIds) => {
    logger.log('[SEJOUR] Updating partenaires for séjour', id, partenaireIds);
    return api.post(`/sejours/${id}/update_partenaires/`, { partenaires: partenaireIds });
  },
  verifierArchivable: (id) => {
    logger.log('[SEJOUR] Vérification si séjour archivable', id);
    return api.get(`/sejours/${id}/verifier_archivable/`);
  },
  archiver: (id) => {
    logger.log('[SEJOUR] Archivage du séjour', id);
    return api.post(`/sejours/${id}/archiver/`);
  },
  statistiquesArchive: (id) => {
    logger.log('[SEJOUR] Statistiques séjour archivé', id);
    return api.get(`/sejours/${id}/statistiques_archive/`);
  },
};

// Partenaires
export const partenaireService = {
  getAll: (sejourId = null) => {
    logger.log('[PARTENAIRE] Getting all partenaires', sejourId ? `for sejour ${sejourId}` : '');
    const url = sejourId ? `/partenaires/?sejour_id=${sejourId}` : '/partenaires/';
    return api.get(url);
  },
  getOne: (id) => {
    logger.log('[PARTENAIRE] Getting partenaire', id);
    return api.get(`/partenaires/${id}/`);
  },
  create: (data) => {
    logger.log('[PARTENAIRE] Creating partenaire', data);
    return api.post('/partenaires/', data);
  },
  update: (id, data) => {
    logger.log('[PARTENAIRE] Updating partenaire', id, data);
    return api.put(`/partenaires/${id}/`, data);
  },
  delete: (id) => {
    logger.log('[PARTENAIRE] Deleting partenaire', id);
    return api.delete(`/partenaires/${id}/`);
  },
};

// Enfants
export const enfantService = {
  getAll: () => {
    logger.log('[ENFANT] Getting all enfants');
    return api.get('/enfants/');
  },
  getOne: (id) => {
    logger.log('[ENFANT] Getting enfant', id);
    return api.get(`/enfants/${id}/`);
  },
  search: (query) => {
    logger.log('[ENFANT] Searching enfants with query:', query);
    return api.get(`/enfants/?search=${encodeURIComponent(query)}`);
  },
  create: (data) => {
    logger.log('[ENFANT] Creating enfant', data);
    // Si data est un FormData, utiliser multipart/form-data
    const isFormData = data instanceof FormData;
    return api.post('/enfants/', data, isFormData ? {
      headers: { 'Content-Type': 'multipart/form-data' },
    } : {});
  },
  update: (id, data) => {
    logger.log('[ENFANT] Updating enfant', id, data);
    // Si data est un FormData, utiliser multipart/form-data
    const isFormData = data instanceof FormData;
    return api.put(`/enfants/${id}/`, data, isFormData ? {
      headers: { 'Content-Type': 'multipart/form-data' },
    } : {});
  },
  delete: (id) => {
    logger.log('[ENFANT] Deleting enfant', id);
    return api.delete(`/enfants/${id}/`);
  },
};

// Inscriptions
export const inscriptionService = {
  getAll: (params = {}) => {
    logger.log('[INSCRIPTION] Getting all inscriptions with params', params);
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/inscriptions/${queryString ? `?${queryString}` : ''}`);
  },
  getOne: (id) => {
    logger.log('[INSCRIPTION] Getting inscription', id);
    return api.get(`/inscriptions/${id}/`);
  },
  create: (data) => {
    logger.log('[INSCRIPTION] Creating inscription', data);
    return api.post('/inscriptions/', data);
  },
  update: (id, data) => {
    logger.log('[INSCRIPTION] *** UPDATING INSCRIPTION ***', id);
    logger.log('[INSCRIPTION] Data being sent:', data);
    return api.patch(`/inscriptions/${id}/`, data);
  },
  delete: (id) => {
    logger.log('[INSCRIPTION] Deleting inscription', id);
    return api.delete(`/inscriptions/${id}/`);
  },
  cancel: (id) => {
    logger.log('[INSCRIPTION] Canceling inscription', id);
    return api.post(`/inscriptions/${id}/cancel/`);
  },
  genererPdf: (id) => {
    logger.log('[INSCRIPTION] Generating PDF for inscription', id);
    return api.get(`/inscriptions/${id}/generer_pdf/`, { responseType: 'blob' });
  },
  updateStatut: (id, statut) => {
    logger.log('[INSCRIPTION] Updating statut for inscription', id, 'to', statut);
    return api.post(`/inscriptions/${id}/update_statut/`, { statut });
  },
  addRemarque: (id, remarque) => {
    logger.log('[INSCRIPTION] Adding remarque to inscription', id);
    return api.post(`/inscriptions/${id}/add_remarque/`, { remarque });
  },
  setAide: (id, aide_accordee, aide_commentaire) => {
    logger.log('[INSCRIPTION] *** SET AIDE APPELÉ ***');
    logger.log('[INSCRIPTION] Inscription ID:', id);
    logger.log('[INSCRIPTION] Aide accordée:', aide_accordee);
    logger.log('[INSCRIPTION] Commentaire:', aide_commentaire);

    logger.log('%c=== SET AIDE FRONTEND ===', 'background: green; color: white; font-size: 16px; padding: 4px;');
    logger.log('  Inscription:', id);
    logger.log('  Aide:', aide_accordee, '€');
    logger.log('  Commentaire:', aide_commentaire);

    return api.post(`/inscriptions/${id}/set_aide/`, { aide_accordee, aide_commentaire });
  },
  setStatutVerificationCaf: (id, statut) => {
    logger.log('[INSCRIPTION] Setting statut vérification CAF for inscription', id);
    logger.log('[INSCRIPTION] Statut:', statut);
    return api.post(`/inscriptions/${id}/set_statut_verification_caf/`, { statut });
  },
  completeInfos: (id, data) => {
    logger.log('[INSCRIPTION] Completing infos for inscription', id);
    logger.log('[INSCRIPTION] Data:', data);
    return api.post(`/inscriptions/${id}/complete_infos/`, data);
  },
  exporterCsv: (sejourId) => {
    logger.log('[INSCRIPTION] Exporting CSV for séjour', sejourId);
    return api.get(`/inscriptions/exporter_csv/?sejour_id=${sejourId}`, { responseType: 'blob' });
  },
  exporterZip: (sejourId) => {
    logger.log('[INSCRIPTION] Exporting ZIP for séjour', sejourId);
    return api.get(`/inscriptions/exporter_zip/?sejour_id=${sejourId}`, { responseType: 'blob' });
  },
  tableauBord: (sejourId) => {
    logger.log('[INSCRIPTION] Getting tableau bord for séjour', sejourId);
    return api.get(`/inscriptions/tableau_bord/${sejourId ? `?sejour_id=${sejourId}` : ''}`);
  },
  validerPartenaire: (id) => {
    logger.log('[INSCRIPTION] Validating partenaire inscription', id);
    return api.post(`/inscriptions/${id}/valider_partenaire/`);
  },
  refuserPartenaire: (id, motif) => {
    logger.log('[INSCRIPTION] Refusing partenaire inscription', id);
    return api.post(`/inscriptions/${id}/refuser_partenaire/`, { motif });
  },
  reactiverInscription: (id) => {
    logger.log('[INSCRIPTION] Reactivating inscription', id);
    return api.post(`/inscriptions/${id}/reactiver_inscription/`);
  },
  getMesInscriptionsArchivees: () => {
    logger.log('[INSCRIPTION] Getting mes inscriptions archivées');
    return api.get('/inscriptions/mes_inscriptions_archivees/');
  },
  getInscriptionsFiltrees: (params = {}) => {
    logger.log('[INSCRIPTION] Getting inscriptions filtrées with params', params);
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/inscriptions/inscriptions_filtrees/${queryString ? `?${queryString}` : ''}`);
  },
  rechercheEnfant: (params = {}, config = {}) => {
    logger.log('[INSCRIPTION] Searching enfants with params', params);
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/inscriptions/recherche_enfant/${queryString ? `?${queryString}` : ''}`, config);
  },
  updateTrancheAge: (id, tranchePrixId) => {
    logger.log('[INSCRIPTION] Updating tranche age for inscription', id);
    logger.log('[INSCRIPTION] New tranche prix ID:', tranchePrixId);
    return api.post(`/inscriptions/${id}/update_tranche_age/`, { tranche_prix_id: tranchePrixId });
  },
};

// Paiements
export const paiementService = {
  getAll: () => {
    logger.log('[PAIEMENT] Getting all paiements');
    return api.get('/paiements/');
  },
  getOne: (id) => {
    logger.log('[PAIEMENT] Getting paiement', id);
    return api.get(`/paiements/${id}/`);
  },
  create: (data) => {
    logger.log('[PAIEMENT] Creating paiement', data);
    return api.post('/paiements/', data);
  },
  update: (id, data) => {
    logger.log('[PAIEMENT] Updating paiement', id, data);
    return api.patch(`/paiements/${id}/`, data);
  },
  delete: (id) => {
    logger.log('[PAIEMENT] Deleting paiement', id);
    return api.delete(`/paiements/${id}/`);
  },
  parInscription: (inscriptionId) => {
    logger.log('[PAIEMENT] Getting paiements for inscription', inscriptionId);
    return api.get(`/paiements/par_inscription/?inscription_id=${inscriptionId}`);
  },
  creerEcheancier: (data) => {
    logger.log('[PAIEMENT] Creating échéancier', data);
    return api.post('/paiements/creer_echeancier/', data);
  },
  valider: (id) => {
    logger.log('[PAIEMENT] Validating paiement', id);
    return api.post(`/paiements/${id}/valider/`);
  },
  refuser: (id, commentaire) => {
    logger.log('[PAIEMENT] Refusing paiement', id);
    return api.post(`/paiements/${id}/refuser/`, { commentaire });
  },
  annulerValidation: (id) => {
    logger.log('[PAIEMENT] Canceling validation for paiement', id);
    return api.post(`/paiements/${id}/annuler_validation/`);
  },
  initierPaiementHelloAsso: (id) => {
    logger.log('[PAIEMENT] Initiating HelloAsso payment for paiement', id);
    return api.post(`/paiements/${id}/initier_paiement_helloasso/`);
  },
  callbackHelloAsso: (params) => {
    logger.log('[PAIEMENT] Processing HelloAsso callback', params);
    // SÉCURITÉ: Utilise POST pour ne pas exposer le token dans les logs serveur
    return api.post(`/paiements/callback_helloasso/`, params);
  },
  supprimerEcheancier: (inscriptionId) => {
    logger.log('[PAIEMENT] Deleting échéancier for inscription', inscriptionId);
    return api.post('/paiements/supprimer_echeancier/', { inscription_id: inscriptionId });
  },
  // Config HelloAsso
  configHelloAsso: {
    get: () => {
      logger.log('[PAIEMENT] Config HA: get');
      return api.get('/config/helloasso/');
    },
    requestEdit: () => {
      logger.log('[PAIEMENT] Config HA: request edit');
      return api.post('/config/helloasso/request_edit/');
    },
    verifyEdit: (code) => {
      logger.log('[PAIEMENT] Config HA: verify edit', code);
      return api.post('/config/helloasso/verify_edit/', { code });
    },
    update: (data) => {
      logger.log('[PAIEMENT] Config HA: update', data);
      return api.post('/config/helloasso/update_config/', data);
    }
  },
};

// Aides financières
export const aideService = {
  getAll: () => {
    logger.log('[AIDE] Getting all aides');
    return api.get('/aides/');
  },
  getOne: (id) => {
    logger.log('[AIDE] Getting aide', id);
    return api.get(`/aides/${id}/`);
  },
  create: (data) => {
    logger.log('[AIDE] Creating aide', data);
    return api.post('/aides/', data);
  },
  update: (id, data) => {
    logger.log('[AIDE] Updating aide', id, data);
    return api.patch(`/aides/${id}/`, data);
  },
  delete: (id) => {
    logger.log('[AIDE] Deleting aide', id);
    return api.delete(`/aides/${id}/`);
  },
  parInscription: (inscriptionId) => {
    logger.log('[AIDE] Getting aides for inscription', inscriptionId);
    return api.get(`/aides/par_inscription/?inscription_id=${inscriptionId}`);
  },
  valider: (id) => {
    logger.log('[AIDE] Validating aide', id);
    return api.post(`/aides/${id}/valider/`);
  },
  refuser: (id, commentaire) => {
    logger.log('[AIDE] Refusing aide', id);
    return api.post(`/aides/${id}/refuser/`, { commentaire });
  },
  validerAccord: (id, commentaire = '') => {
    logger.log('[AIDE] Validating accord for aide', id);
    return api.post(`/aides/${id}/valider_accord/`, { commentaire });
  },
  refuserAccord: (id, commentaire) => {
    logger.log('[AIDE] Refusing accord for aide', id);
    return api.post(`/aides/${id}/refuser_accord/`, { commentaire });
  },
  validerPaiement: (id, commentaire = '') => {
    logger.log('[AIDE] Validating paiement for aide', id);
    return api.post(`/aides/${id}/valider_paiement/`, { commentaire });
  },
  refuserPaiement: (id, commentaire) => {
    logger.log('[AIDE] Refusing paiement for aide', id);
    return api.post(`/aides/${id}/refuser_paiement/`, { commentaire });
  },
  annulerAccord: (id) => {
    logger.log('[AIDE] Canceling accord for aide', id);
    return api.post(`/aides/${id}/annuler_accord/`);
  },
  annulerPaiement: (id) => {
    logger.log('[AIDE] Canceling paiement for aide', id);
    return api.post(`/aides/${id}/annuler_paiement/`);
  },
};

// Documents
export const documentService = {
  getAll: () => {
    logger.log('[DOCUMENT] Getting all documents');
    return api.get('/documents/');
  },
  getOne: (id) => {
    logger.log('[DOCUMENT] Getting document', id);
    return api.get(`/documents/${id}/`);
  },
  parInscription: (inscriptionId) => {
    logger.log('[DOCUMENT] Getting documents for inscription', inscriptionId);
    return api.get(`/documents/par_inscription/?inscription_id=${inscriptionId}`);
  },
  upload: (formData) => {
    logger.log('[DOCUMENT] Uploading document');
    return api.post('/documents/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (id) => {
    logger.log('[DOCUMENT] Deleting document', id);
    return api.delete(`/documents/${id}/`);
  },
  remplacer: (id, formData) => {
    logger.log('[DOCUMENT] Replacing document', id);
    return api.post(`/documents/${id}/remplacer/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  valider: (id, commentaire) => {
    logger.log('[DOCUMENT] Validating document', id);
    return api.post(`/documents/${id}/valider/`, { commentaire });
  },
  refuser: (id, commentaire) => {
    logger.log('[DOCUMENT] Refusing document', id);
    return api.post(`/documents/${id}/refuser/`, { commentaire });
  },
  marquerNonObtenu: (id, commentaire = '') => {
    logger.log('[DOCUMENT] Marking document as non-obtenu', id);
    return api.post(`/documents/${id}/marquer_non_obtenu/`, { commentaire });
  },
  annulerValidation: (id) => {
    logger.log('[DOCUMENT] Canceling validation for document', id);
    return api.post(`/documents/${id}/annuler_validation/`);
  },
  getModeleFicheSanitaire: () => {
    logger.log('[DOCUMENT] Getting modele fiche sanitaire');
    return api.get('/documents/modele_fiche_sanitaire/', { responseType: 'blob' });
  },
};

// Logs
export const logService = {
  getLogsParents: (params = {}) => {
    logger.log('[LOG] Getting logs parents', params);
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/logs/parents/${queryString ? `?${queryString}` : ''}`);
  },
  getLogsAdmins: (params = {}) => {
    logger.log('[LOG] Getting logs admins', params);
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/logs/admins/${queryString ? `?${queryString}` : ''}`);
  },
  purgeLogsParents: (days, keepErrors = true, keepCritical = true) => {
    logger.log('[LOG] Purging logs parents', { days, keepErrors, keepCritical });
    return api.post('/logs/parents/purge/', { days, keep_errors: keepErrors, keep_critical: keepCritical });
  },
  purgeLogsAdmins: (days, keepErrors = true, keepCritical = true) => {
    logger.log('[LOG] Purging logs admins', { days, keepErrors, keepCritical });
    return api.post('/logs/admins/purge/', { days, keep_errors: keepErrors, keep_critical: keepCritical });
  },
  getStatsParents: () => {
    logger.log('[LOG] Getting stats parents');
    return api.get('/logs/parents/stats/');
  },
  getStatsAdmins: () => {
    logger.log('[LOG] Getting stats admins');
    return api.get('/logs/admins/stats/');
  },
};

// Emails
export const emailsService = {
  // Templates
  getTemplates: () => {
    logger.log('[EMAILS] Getting all templates');
    return api.get('/emails/templates/');
  },
  getTemplate: (id) => {
    logger.log('[EMAILS] Getting template', id);
    return api.get(`/emails/templates/${id}/`);
  },
  createTemplate: (data) => {
    logger.log('[EMAILS] Creating template', data);
    return api.post('/emails/templates/', data);
  },
  updateTemplate: (id, data) => {
    logger.log('[EMAILS] Updating template', id, data);
    return api.patch(`/emails/templates/${id}/`, data);
  },
  deleteTemplate: (id) => {
    logger.log('[EMAILS] Deleting template', id);
    return api.delete(`/emails/templates/${id}/`);
  },
  dupliquerTemplate: (id) => {
    logger.log('[EMAILS] Duplicating template', id);
    return api.post(`/emails/templates/${id}/dupliquer/`);
  },
  previewTemplate: (id, inscriptionId = null) => {
    logger.log('[EMAILS] Previewing template', id, 'with inscription', inscriptionId);
    return api.post(`/emails/templates/${id}/preview/`, { inscription_id: inscriptionId });
  },
  getVariablesDisponibles: () => {
    logger.log('[EMAILS] Getting available variables');
    return api.get('/emails/templates/variables_disponibles/');
  },

  // Logs
  getLogs: (filters = {}) => {
    logger.log('[EMAILS] Getting logs with filters', filters);
    const queryString = new URLSearchParams(filters).toString();
    return api.get(`/emails/logs/${queryString ? `?${queryString}` : ''}`);
  },
  getLog: (id) => {
    logger.log('[EMAILS] Getting log', id);
    return api.get(`/emails/logs/${id}/`);
  },
  renvoyerEmail: (id) => {
    logger.log('[EMAILS] Resending email from log', id);
    return api.post(`/emails/logs/${id}/renvoyer/`);
  },
  getStatistiques: () => {
    logger.log('[EMAILS] Getting statistics');
    return api.get('/emails/logs/statistiques/');
  },

  // Settings
  getSettings: () => {
    logger.log('[EMAILS] Getting SMTP settings');
    return api.get('/emails/settings/');
  },
  getSetting: (id) => {
    logger.log('[EMAILS] Getting SMTP setting', id);
    return api.get(`/emails/settings/${id}/`);
  },
  createSettings: (data) => {
    logger.log('[EMAILS] Creating SMTP settings', data);
    return api.post('/emails/settings/', data);
  },
  updateSettings: (id, data) => {
    logger.log('[EMAILS] Updating SMTP settings', id, data);
    return api.patch(`/emails/settings/${id}/`, data);
  },
  deleteSettings: (id) => {
    logger.log('[EMAILS] Deleting SMTP settings', id);
    return api.delete(`/emails/settings/${id}/`);
  },
  testerConfiguration: (id) => {
    logger.log('[EMAILS] Testing SMTP configuration', id);
    return api.post(`/emails/settings/${id}/tester/`);
  },
  activerConfiguration: (id) => {
    logger.log('[EMAILS] Activating SMTP configuration', id);
    return api.post(`/emails/settings/${id}/activer/`);
  },

  // Envoi
  envoyerEmail: (data) => {
    logger.log('[EMAILS] Sending email', data);
    return api.post('/emails/envoi/envoyer/', data);
  },
  envoyerTemplate: (data) => {
    logger.log('[EMAILS] Sending email from template', data);
    return api.post('/emails/envoi/envoyer_template/', data);
  },
  previewEmail: (data) => {
    logger.log('[EMAILS] Previewing email', data);
    return api.post('/emails/envoi/preview/', data);
  },
  envoyerMasse: (data) => {
    logger.log('[EMAILS] Sending bulk emails', data);
    return api.post('/emails/envoi/envoyer_masse/', data);
  },
};

export default api;
